import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getProducts, getBundles } from "../../lib/catalogCache";
import api from "../../lib/api";
import PublicHeader from "../../components/layout/PublicHeader";
import PublicFooter from "../../components/layout/PublicFooter";
import PluginCard from "../../components/PluginCard";
import { Input } from "../../components/ui/input";
import { formatCLP, formatUSD } from "../../lib/format";

export default function Catalog() {
    const [products, setProducts] = useState([]);
    const [bundles, setBundles] = useState([]);
    const [categories, setCategories] = useState([]);
    const [activeCat, setActiveCat] = useState("");
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([getProducts(), getBundles()])
            .then(([p, b]) => {
                setProducts(p || []);
                setBundles(b || []);
            })
            .finally(() => setLoading(false));
        api.get("/categories").then(({ data }) => setCategories((data || []).filter((c) => c.product_count > 0))).catch(() => {});
    }, []);

    const filtered = products.filter((p) =>
        (!q || p.name?.toLowerCase().includes(q.toLowerCase()) || p.short_description?.toLowerCase().includes(q.toLowerCase()))
        && (!activeCat || (p.category_ids || []).includes(activeCat))
    );
    const filteredBundles = bundles.filter((b) =>
        !q || b.name?.toLowerCase().includes(q.toLowerCase()) || b.short_description?.toLowerCase().includes(q.toLowerCase())
    );

    return (
        <div className="min-h-screen flex flex-col" data-testid="catalog-page">
            <PublicHeader />
            <section className="border-b border-avx-line">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
                    <div className="label-mono mb-4">/ Catalog</div>
                    <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter mb-6">
                        Plugin <span className="text-avx-blue">catalog</span>
                    </h1>
                    <p className="text-avx-muted max-w-2xl mb-8">
                        Find the processing you need for mixing, mastering, FOH and broadcast. Compatible with VST3, AU and AAX for Windows and macOS.
                    </p>
                    <div className="max-w-md">
                        <Input
                            placeholder="Search plugin or bundle…"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            data-testid="catalog-search-input"
                            className="bg-avx-surface border-avx-line h-11 text-sm focus:border-avx-blue focus:ring-1 focus:ring-avx-blue"
                        />
                    </div>
                </div>
            </section>

            {filteredBundles.length > 0 && (
                <section className="border-b border-avx-line bg-avx-surface/30" data-testid="bundles-section">
                    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
                        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
                            <div>
                                <div className="label-mono mb-2 text-avx-blue">/ Bundles & deals</div>
                                <h2 className="font-heading font-bold text-2xl lg:text-3xl tracking-tight">Combined packs</h2>
                            </div>
                            <div className="text-xs font-mono text-avx-muted">Save by buying several plugins together</div>
                        </div>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {filteredBundles.map((b) => (
                                <Link to={`/bundles/${b.slug}`} key={b.id} className="panel-elevated p-6 flex flex-col hover:border-avx-blue/50 transition-colors group" data-testid={`bundle-public-${b.id}`}>
                                    <div className="flex items-start justify-between gap-3 mb-3">
                                        <div>
                                            <h3 className="font-heading font-bold text-xl mb-1 group-hover:text-avx-blue transition-colors">{b.name}</h3>
                                            <div className="label-mono">{b.products?.length || 0} plugins · BUNDLE</div>
                                        </div>
                                        {b.badge_text && (
                                            <span className="px-2 py-1 rounded-sm bg-avx-blue/15 border border-avx-blue/40 text-avx-blue text-[10px] font-mono uppercase tracking-[0.2em] shrink-0">
                                                {b.badge_text}
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-sm text-avx-muted mb-4 leading-relaxed line-clamp-3">{b.short_description}</p>
                                    <div className="flex flex-wrap gap-1.5 mb-5">
                                        {(b.products || []).slice(0, 5).map((p) => (
                                            <span key={p.id} className="px-2 py-0.5 rounded-sm bg-avx-bg/60 border border-avx-line text-[10px] font-mono">{p.name}</span>
                                        ))}
                                    </div>
                                    {b.sale_mode === "subscription" ? (
                                        <div className="flex items-end gap-3 mt-auto pt-3 border-t border-avx-line" data-testid={`bundle-sub-pricing-${b.id}`}>
                                            <div>
                                                <div className="label-mono text-avx-blue mb-0.5">Suscripción</div>
                                                {(() => {
                                                    const order = ["monthly", "semiannual", "annual"];
                                                    const clp = order.map((k) => b.sub_prices?.[k]?.clp || 0).find((v) => v > 0) || 0;
                                                    const usd = order.map((k) => b.sub_prices?.[k]?.usd || 0).find((v) => v > 0) || 0;
                                                    return (
                                                        <>
                                                            {clp > 0 ? (
                                                                <div className="font-mono text-avx-blue font-bold text-lg">Desde {formatCLP(clp)}<span className="text-[10px] text-avx-muted">/mes</span></div>
                                                            ) : usd > 0 ? (
                                                                <div className="font-mono text-avx-blue font-bold text-lg">Desde {formatUSD(usd)}<span className="text-[10px] text-avx-muted">/mes</span></div>
                                                            ) : null}
                                                            {clp > 0 && usd > 0 && <div className="font-mono text-[10px] text-avx-muted">Desde {formatUSD(usd)}/mes</div>}
                                                        </>
                                                    );
                                                })()}
                                            </div>
                                            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-avx-blue bg-avx-blue/15 px-2 py-1 rounded-sm border border-avx-blue/40 ml-auto">
                                                SUSCRIPCIÓN
                                            </div>
                                        </div>
                                    ) : (
                                    <div className="flex items-end gap-3 mt-auto pt-3 border-t border-avx-line">
                                        <div>
                                            <div className="font-mono text-avx-blue font-bold text-lg">{formatCLP(b.price_clp)}</div>
                                            {b.original_price_clp > b.price_clp && (
                                                <div className="font-mono text-[10px] text-avx-muted line-through">{formatCLP(b.original_price_clp)}</div>
                                            )}
                                            {b.price_usd > 0 && <div className="font-mono text-[10px] text-avx-muted">{formatUSD(b.price_usd)}</div>}
                                        </div>
                                        {b.discount_pct > 0 && (
                                            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-avx-success bg-avx-success/15 px-2 py-1 rounded-sm border border-avx-success/40 ml-auto">
                                                −{b.discount_pct}%
                                            </div>
                                        )}
                                    </div>
                                    )}
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            <section className="max-w-7xl mx-auto px-6 lg:px-10 py-14 flex-1 w-full">
                <div className="label-mono mb-6">/ Individual plugins</div>
                {categories.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-8" data-testid="catalog-category-filter">
                        <button
                            onClick={() => setActiveCat("")}
                            data-testid="catalog-cat-all"
                            className={`px-3 py-1.5 rounded-sm text-[11px] font-mono uppercase tracking-[0.15em] border transition-colors ${!activeCat ? "border-avx-blue bg-avx-blue/15 text-avx-blue" : "border-avx-line text-avx-muted hover:border-avx-blue/40"}`}
                        >
                            Todas
                        </button>
                        {categories.map((c) => (
                            <button
                                key={c.id}
                                onClick={() => setActiveCat(c.id)}
                                data-testid={`catalog-cat-${c.slug}`}
                                className={`px-3 py-1.5 rounded-sm text-[11px] font-mono uppercase tracking-[0.15em] border transition-colors ${activeCat === c.id ? "border-avx-blue bg-avx-blue/15 text-avx-blue" : "border-avx-line text-avx-muted hover:border-avx-blue/40"}`}
                            >
                                {c.name} <span className="opacity-60">({c.product_count})</span>
                            </button>
                        ))}
                    </div>
                )}
                {loading ? (
                    <div className="text-avx-muted font-mono">Loading…</div>
                ) : filtered.length === 0 ? (
                    <div className="text-avx-muted font-mono">No plugins found.</div>
                ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filtered.map((p) => <PluginCard key={p.id} product={p} />)}
                    </div>
                )}
            </section>
            <PublicFooter />
        </div>
    );
}
